import React from 'react';
import classNames from 'classnames';

import { getEntityUrl } from 'util/navHelpers';
import { observer, inject } from 'mobx-react';
import { navigate } from '@reach/router';

const getSubjectsText = ({ subjects }) => {
  if (!subjects) return;
  return ` + ${subjects.length - 1} other${subjects?.length > 2 ? 's' : ''}`;
};

const SubjectText = ({ children }) => {
  return (
    <span className="actor-verb-subject">
      <span className="subject-text">{children}</span>
    </span>
  );
};

const ActorVerbSubjectText = ({
  actor,
  event,
  subjects,
  projects,
  purchaseCount,
  type,
  initiator,
  joinedEntity,
  eventType,
  isMatchSponsor,
  entity,
  activityPurchase,
  isAuthenticated,
  uiStore,
  isCauzePage,
}) => {
  const isActorAlsoInitiator =
    actor.id === initiator.id && actor.entityType === initiator.entityType;
  const isActorAlsoJoined =
    actor?.id === joinedEntity?.id && actor?.entityType === joinedEntity?.type;
  const isInactiveUser =
    actor.entityType === 'USER' &&
    (actor.user?.unclaimed || !actor.user?.isActive);
  const entityUrl = isInactiveUser
    ? undefined
    : getEntityUrl({ id: actor.id, entityType: actor.entityType });
  const matchCount = activityPurchase?.matchPurchases
    ?.filter((mp) => `${mp?.matchSponsor?.id}` === `${entity?.id}`)
    ?.reduce((a, b) => {
      return a + (b?.match?.donorCount || 0);
    }, 0);

  const handleLinkClick = (e, host) => {
    if (host) {
      e.preventDefault();
      e.stopPropagation();
      if (host.notAllowed) {
        return;
      }
      uiStore.openModal('PROFILE_MODAL', {
        id: host.id,
        entityType: host.entityType,
      });
    }
  };
  const handleEventOrProjectClick = (e, url) => {
    e.preventDefault();
    e.stopPropagation();

    navigate(url);
  };

  const renderActorDisplay = () => {
    if (isMatchSponsor) {
      const NameWrapper = !entityUrl ? 'span' : 'a';
      const matcherUrl = getEntityUrl({
        id: entity.id,
        entityType: entity.groupType,
      });
      return (
        <NameWrapper
          className={classNames('actor-name', {
            'brand-hover': Boolean(matcherUrl),
          })}
          href={matcherUrl}
          onClick={(e) => {
            handleLinkClick(e, {
              notAllowed: !entityUrl,
              id: entity.id,
              entityType: entity.groupType.toUpperCase(),
            });
          }}
        >
          {entity.name}
        </NameWrapper>
      );
    }

    if (actor.isSelf && isAuthenticated) {
      return <span className="actor-name">You</span>;
    }

    const NameWrapper = !entityUrl ? 'span' : 'a';

    return (
      <NameWrapper
        className={classNames('actor-name', {
          'brand-hover': Boolean(entityUrl),
        })}
        onClick={(e) => {
          handleLinkClick(e, {
            notAllowed: !entityUrl,
            id: actor.id,
            entityType: actor.entityType.toUpperCase(),
          });
        }}
      >
        {actor.name}
      </NameWrapper>
    );
  };

  const renderAdditionalActors = () => {
    if (isCauzePage || isMatchSponsor) return <></>;
    if (type?.toUpperCase() === 'INITIATED_ACTIVITY' || purchaseCount < 3) {
      return <></>;
    }
    return (
      <span>
        {` and ${purchaseCount - 1} other${purchaseCount > 2 ? 's' : ''}`}
      </span>
    );
  };

  const renderJoined = () => {
    if (type?.toUpperCase() === 'INITIATED_ACTIVITY' || purchaseCount < 1) {
      return <></>;
    }

    if (isMatchSponsor) {
      const NameWrapper = !entityUrl ? 'span' : 'a';

      return (
        <span>
          {' '}
          has matched{' '}
          <NameWrapper
            className={classNames('actor-name', {
              'brand-hover': Boolean(entityUrl),
            })}
            onClick={(e) => {
              handleLinkClick(e, {
                notAllowed: !entityUrl,
                id: actor.id,
                entityType: actor.entityType.toUpperCase(),
              });
            }}
          >
            {actor.name}{' '}
          </NameWrapper>
          {matchCount > 2 && !isCauzePage && (
            <span>
              {` and ${matchCount - 1} other${matchCount > 2 ? 's' : ''}`}
            </span>
          )}
        </span>
      );
    }

    if (initiator.isSelf && !isActorAlsoInitiator && isAuthenticated) {
      return <span> joined you</span>;
    }

    const eventHost = event?.hosts?.[0];
    if (!joinedEntity && eventHost) {
      const joinedUrl = getEntityUrl({
        id: eventHost?.id,
        entityType: eventHost?.type,
      });

      const NameWrapper = !joinedUrl ? 'span' : 'a';

      return (
        <>
          {' '}
          joined{' '}
          <NameWrapper
            className={classNames('actor-name', {
              'brand-hover': Boolean(joinedUrl),
            })}
            onClick={(e) => {
              handleLinkClick(e, {
                notAllowed: !joinedUrl,

                id: joinedEntity.id,
                entityType: joinedEntity.type.toUpperCase(),
              });
            }}
          >
            {joinedEntity?.name}
          </NameWrapper>
        </>
      );
    }

    if (joinedEntity?.name && !isActorAlsoJoined) {
      const joinedUrl = getEntityUrl({
        id: joinedEntity.id,
        entityType: joinedEntity.type,
      });

      const NameWrapper = !joinedUrl ? 'span' : 'a';

      return (
        <>
          {' '}
          joined{' '}
          <NameWrapper
            className={classNames('actor-name', {
              'brand-hover': Boolean(joinedUrl),
            })}
            onClick={(e) => {
              handleLinkClick(e, {
                notAllowed: !joinedUrl,
                id: joinedEntity.id,
                entityType: joinedEntity.type.toUpperCase(),
              });
            }}
          >
            {joinedEntity?.name}
          </NameWrapper>
        </>
      );
    }

    return <></>;
  };

  const renderSupportOrStarted = () => {
    if (type === 'JOINED_ACTIVITY' && !isActorAlsoJoined) {
      return joinedEntity || event ? ', in supporting ' : ' supported ';
    } else if (type === 'INITIATED_ACTIVITY') {
      if (subjects?.[0] && eventType?.toUpperCase() === 'PLANNED') {
        return ' supported ';
      } else {
        return ' supported ';
      }
    } else {
      return ' supported ';
    }
  };

  const renderEventOrCharitiesTitle = () => {
    const event = subjects?.[0];
    const eventUrl = event ? `/event/${event?.id}` : undefined;
    const projectUrl = projects?.[0]?.id
      ? `/charity/${projects?.[0]?.id}`
      : undefined;
    const NameWrapper = !eventUrl ? 'span' : 'a';
    const ProjectNameWrapper = !projectUrl ? 'span' : 'a';

    if (event?.eventType?.toLowerCase() === 'planned') {
      return (
        <NameWrapper
          className={classNames('actor-name', {
            'brand-hover': Boolean(eventUrl),
          })}
          href={eventUrl}
          onClick={(e) => {
            handleEventOrProjectClick(e, eventUrl);
          }}
        >
          {event.name}{' '}
        </NameWrapper>
      );
    } else if (projectUrl) {
      return (
        <ProjectNameWrapper
          className={classNames('actor-name', {
            'brand-hover': Boolean(projectUrl),
          })}
          href={projectUrl}
          onClick={(e) => {
            handleEventOrProjectClick(e, projectUrl);
          }}
        >
          <span>{projects?.[0]?.name}</span>
          <span>
            {projects?.length > 1 && (
              <span>{getSubjectsText({ subjects: projects })}</span>
            )}
          </span>
        </ProjectNameWrapper>
      );
    } else if (event) {
      return (
        <NameWrapper
          className={classNames('actor-name', {
            'brand-hover': Boolean(eventUrl),
          })}
          href={eventUrl}
          onClick={(e) => {
            handleEventOrProjectClick(e, eventUrl);
          }}
        >
          {event.name}{' '}
        </NameWrapper>
      );
    } else {
      return <></>;
    }
  };

  return (
    <SubjectText>
      {renderActorDisplay()}
      {renderAdditionalActors()}
      {renderJoined()}
      {renderSupportOrStarted()}
      {renderEventOrCharitiesTitle()}
    </SubjectText>
  );
};

export default inject('uiStore')(observer(ActorVerbSubjectText));
